import React, { useState, useCallback, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import CustomTextInput from '@/components/CustomTextInput'
import CustomButton from '@/components/CustomButton'
import CountryCodeSelector from '@/components/CountryCodeSelector'
import { CardActionType } from '../index.type'
import { useSendCode } from '@/hooks/useSendCode'
import { Toast } from 'react-vant'
import { APIWeb } from '@/api'


export default React.memo(function EnterAccount() {
    const navigate = useNavigate()
    const { state } = useLocation()
    console.log('EnterAccount', state)
    const { t } = useTranslation()
    const [countryCode, setCountryCode] = useState('+86')
    const { sendCode, sendCodeLoading } = useSendCode()
    const [email, setEmail] = useState('')
    const [phone, setPhone] = useState('')
    const [errors, setErrors] = useState<{ email?: string; phone?: string }>({})


    // 获取当前操作类型
    const accountType = state?.accountType as CardActionType

    // 邮箱验证函数
    const validateEmail = useCallback((email: string): string => {
        if (!email) {
            return t('请输入邮箱地址')
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(email)) {
            return t('请输入有效的邮箱地址')
        }
        return ''
    }, [t])

    // 手机号验证函数（不包含区号）
    const validatePhone = useCallback((phone: string): string => {
        if (!phone) {
            return t('请输入手机号')
        }
        // 验证纯数字手机号：6-15位数字
        const phoneRegex = /^\d{6,15}$/
        if (!phoneRegex.test(phone)) {
            return t('请输入有效的手机号6至15位数字')
        }
        return ''
    }, [t])

    // 处理邮箱输入变化
    const handleEmailChange = useCallback((value: string) => {
        setEmail(value)
        // 清除之前的错误信息
        if (errors.email) {
            setErrors(prev => ({ ...prev, email: undefined }))
        }
        // 实时验证格式
        const error = validateEmail(value)
        if (error) {
            setErrors(prev => ({ ...prev, email: error }))
        }
    }, [errors.email, validateEmail])

    // 处理手机号输入变化
    const handlePhoneChange = useCallback((value: string) => {
        // 只允许输入数字
        const numericValue = value.replace(/[^\d]/g, '')

        setPhone(numericValue)
        // 清除之前的错误信息
        if (errors.phone) {
            setErrors(prev => ({ ...prev, phone: undefined }))
        }
        // 实时验证
        const error = validatePhone(numericValue)
        if (error) {
            setErrors(prev => ({ ...prev, phone: error }))
        }
    }, [errors.phone, validatePhone])

    // 检查表单是否有效
    const isFormValid = useMemo(() => {
        if (accountType === CardActionType.Email) {
            return email && !validateEmail(email)
        } else if (accountType === CardActionType.Phone) {
            return phone && !validatePhone(phone)
        }
        return false
    }, [accountType, email, phone, validateEmail, validatePhone])

    // 提交验证函数
    const handleSubmit = useCallback(async () => {
        if (accountType === CardActionType.Email) {
            const emailError = validateEmail(email)
            if (emailError) {
                setErrors(prev => ({ ...prev, email: emailError }))
                return
            }
            // 提交前检查账号是否已注册
            try {
                Toast.loading({message: t('验证中'),forbidClick: true})
                const result = await APIWeb.checkAccountStatus({ account: email })
                Toast.clear()
                if (!result?.registered) {
                    Toast.info(t('该邮箱未注册'))
                    return
                }
            } catch (error) {
                console.error('检测账号状态失败:', error)
                Toast.info(t('该邮箱未注册'))
                return
            }
            // 发送验证码
            const data = await sendCode({
                account: email,
                scene: 'password_reset'
            })
            if (data) {
                navigate('/forgot-password/ver-account-code', {
                    state: {
                        account: email,
                        ...state
                    }
                })
            }

        } else if (accountType === CardActionType.Phone) {
            const phoneError = validatePhone(phone)
            if (phoneError) {
                setErrors(prev => ({ ...prev, phone: phoneError }))
                return
            }
            const data = await sendCode({
                account: phone,
                scene: 'password_reset'
            })
            if (data) {
                navigate('/forgot-password/ver-account-code', {
                    state: {
                        account: phone,
                        ...state
                    }
                })
            }

        }
    }, [accountType, email, phone, validateEmail, validatePhone, sendCode, navigate, state, t])

    // 邮箱输入框组件
    const EmailInputView = useMemo(() => (
        <div className="space-y-2">
            <div className="text-sm text-[#1A1A1A]/60 mb-1">{t("请输入需要重置密码的邮箱")}</div>
            <CustomTextInput
                isErrored={Boolean(errors.email)}
                value={email}
                onChangeText={handleEmailChange}
                placeholder={t("请输入邮箱")}
                inputMode='email'
            />
            {errors.email && (
                <div className="text-red-500 text-xs mt-1">{errors.email}</div>
            )}
        </div>
    ), [email, errors.email, handleEmailChange, t])

    // 手机号输入框组件
    const PhoneInputView = useMemo(() => (
        <div className="space-y-2">
            <div className="text-sm text-[#1A1A1A]/60 mb-1">{t("请输入需要重置密码的手机号")}</div>
            <div className='flex justify-between items-center'>
                <div className='w-20 mr-2'>
                    <CountryCodeSelector
                        value={countryCode}
                        onChange={setCountryCode}
                    />
                </div>
                <div className='flex-1'>
                    <CustomTextInput
                        isErrored={Boolean(errors.phone)}
                        value={phone}
                        onChangeText={handlePhoneChange}
                        placeholder={t("请输入手机号")}
                        inputMode='numeric'
                    />
                </div>

            </div>
            {errors.phone && (
                <div className="text-red-500 text-xs mt-1">{errors.phone}</div>
            )}
        </div>
    ), [phone, errors.phone, handlePhoneChange, t])

    // 根据accountType确定页面标题

    return (
        <PageLayout>
            <Header
                leftChildren={<Back />}
            />
            <div className="px-6 py-6">
                <div className="space-y-6">
                    <div className="space-y-4">
                        {accountType === CardActionType.Email && EmailInputView}
                        {accountType === CardActionType.Phone && PhoneInputView}
                    </div>

                    <CustomButton
                        onClick={handleSubmit}
                        loading={sendCodeLoading}
                        disabled={!isFormValid || sendCodeLoading}
                        className={`w-full ${!isFormValid ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        {t("确认")}
                    </CustomButton>
                </div>
            </div>
        </PageLayout>
    )
})